import { useState } from 'react';
import { Layout } from '../components/Layout/Layout';
import {
  checkCameraConnection,
  getCameraSession,
  capturePhoto,
  downloadPhoto,
  getLatestCapturedImage,
  CameraSession,
} from '../service/camera.service';
import styled from '@emotion/styled';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 20px;
  padding: 40px;
  max-width: 800px;
  margin: 0 auto;
`;

const Title = styled.h1`
  font-size: 32px;
  font-weight: bold;
  color: #333;
  margin-bottom: 20px;
`;

const StatusBox = styled.div<{ isConnected: boolean }>`
  padding: 20px;
  border-radius: 8px;
  background-color: ${(props) => (props.isConnected ? '#d4edda' : '#f8d7da')};
  border: 1px solid ${(props) => (props.isConnected ? '#c3e6cb' : '#f5c6cb')};
  color: ${(props) => (props.isConnected ? '#155724' : '#721c24')};
  width: 100%;
  text-align: center;
  font-weight: 600;
`;

const Button = styled.button`
  padding: 15px 30px;
  font-size: 16px;
  font-weight: 600;
  border: none;
  border-radius: 8px;
  background-color: #007bff;
  color: white;
  cursor: pointer;
  transition: background-color 0.2s;

  &:hover {
    background-color: #0056b3;
  }

  &:disabled {
    background-color: #6c757d;
    cursor: not-allowed;
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
  justify-content: center;
`;

const InfoBox = styled.div`
  padding: 20px;
  background-color: #f8f9fa;
  border-radius: 8px;
  width: 100%;
`;

const InfoTitle = styled.h3`
  font-size: 18px;
  font-weight: bold;
  margin-bottom: 10px;
  color: #495057;
`;

const InfoText = styled.p`
  font-size: 14px;
  color: #6c757d;
  margin: 5px 0;
`;

const ImagePreview = styled.img`
  max-width: 100%;
  max-height: 400px;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
`;

export function CameraTestPage() {
  const [isConnected, setIsConnected] = useState<boolean | null>(null);
  const [session, setSession] = useState<CameraSession | null>(null);
  const [capturedImage, setCapturedImage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const handleCheckConnection = async () => {
    setLoading(true);
    setMessage('카메라 연결 확인 중...');
    try {
      const connected = await checkCameraConnection();
      setIsConnected(connected);
      setMessage(connected ? '✅ 카메라 연결 성공!' : '❌ 카메라 연결 실패');
    } catch (error) {
      setIsConnected(false);
      setMessage('❌ 카메라 연결 실패: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleGetSession = async () => {
    setLoading(true);
    setMessage('세션 정보 가져오는 중...');
    try {
      const sessionData = await getCameraSession();
      setSession(sessionData);
      setMessage('✅ 세션 정보 로드 완료');
    } catch (error) {
      setMessage('❌ 세션 정보 로드 실패: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCapture = async () => {
    setLoading(true);
    setMessage('📸 촬영 중...');
    try {
      await capturePhoto();
      setMessage('✅ 촬영 완료! 세션 정보를 업데이트하세요.');
    } catch (error) {
      setMessage('❌ 촬영 실패: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = async () => {
    if (!session) {
      setMessage('❌ 세션 정보가 없습니다. 먼저 세션 정보를 가져오세요.');
      return;
    }

    const latestImageName = getLatestCapturedImage(session);
    if (!latestImageName) {
      setMessage('❌ 다운로드할 이미지가 없습니다. 먼저 촬영하거나 세션 정보를 확인하세요.');
      return;
    }

    setLoading(true);
    setMessage('다운로드 중...');
    try {
      const imageBlob = await downloadPhoto(latestImageName);
      const imageUrl = URL.createObjectURL(imageBlob);
      setCapturedImage(imageUrl);
      setMessage('✅ 이미지 다운로드 완료!');
    } catch (error) {
      setMessage('❌ 다운로드 실패: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCaptureAndDownload = async () => {
    setLoading(true);
    setMessage('📸 촬영 및 다운로드 중...');
    try {
      // 1. 촬영 (DigiCamControl의 HTTP 응답 버그로 500 에러가 나지만 실제 촬영은 성공)
      try {
        await capturePhoto();
      } catch (captureError) {
        // 에러 무시하고 계속 진행 (촬영 자체는 성공함)
      }

      // 2. 잠시 대기 (카메라가 파일을 저장하는 시간)
      await new Promise((resolve) => setTimeout(resolve, 2000));

      // 3. 세션 정보 업데이트
      const sessionData = await getCameraSession();
      setSession(sessionData);

      // 4. 최신 이미지 다운로드
      const latestImageName = getLatestCapturedImage(sessionData);
      if (latestImageName) {
        const imageBlob = await downloadPhoto(latestImageName);
        const imageUrl = URL.createObjectURL(imageBlob);
        setCapturedImage(imageUrl);
        setMessage('✅ 촬영 및 다운로드 완료!');
      } else {
        setMessage('⚠️ 촬영은 완료되었으나 이미지를 찾을 수 없습니다.');
      }
    } catch (error) {
      setMessage('❌ 실패: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <Container>
        <Title>📷 Canon DSLR 카메라 테스트</Title>

        {isConnected !== null && (
          <StatusBox isConnected={isConnected}>
            {isConnected ? '✅ digiCamControl 연결됨' : '❌ digiCamControl 연결 안됨'}
          </StatusBox>
        )}

        <ButtonGroup>
          <Button onClick={handleCheckConnection} disabled={loading}>
            연결 확인
          </Button>
          <Button onClick={handleGetSession} disabled={loading}>
            세션 정보 가져오기
          </Button>
          <Button onClick={handleCapture} disabled={loading || !isConnected}>
            촬영하기
          </Button>
          <Button onClick={handleDownload} disabled={loading || !session || session.Files.length === 0}>
            이미지 다운로드
          </Button>
          <Button
            onClick={handleCaptureAndDownload}
            disabled={loading || !isConnected}
            style={{ backgroundColor: '#28a745' }}
          >
            촬영 + 다운로드
          </Button>
        </ButtonGroup>

        {message && (
          <InfoBox>
            <InfoText>{message}</InfoText>
          </InfoBox>
        )}

        {session && (
          <InfoBox>
            <InfoTitle>세션 정보</InfoTitle>
            <InfoText>📁 폴더: {session.Folder}</InfoText>
            <InfoText>🔢 카운터: {session.Counter}</InfoText>
            <InfoText>📷 촬영된 사진: {session.Files.length}장</InfoText>
            {getLatestCapturedImage(session) && (
              <InfoText>📸 최근 촬영: {getLatestCapturedImage(session)}</InfoText>
            )}
          </InfoBox>
        )}

        {capturedImage && (
          <div style={{ width: '100%' }}>
            <InfoTitle>촬영된 이미지</InfoTitle>
            <ImagePreview src={capturedImage} alt="Captured" />
          </div>
        )}

        <InfoBox>
          <InfoTitle>💡 사용 방법</InfoTitle>
          <InfoText>1. Windows PC에서 digiCamControl 실행 및 HTTP API 활성화</InfoText>
          <InfoText>2. Canon DSLR USB 연결</InfoText>
          <InfoText>3. "연결 확인" 버튼으로 연결 테스트</InfoText>
          <InfoText>4. "촬영 + 다운로드" 버튼으로 간편하게 촬영 및 확인</InfoText>
        </InfoBox>
      </Container>
    </Layout>
  );
}
